import type { FC } from "react";

import { Article, Container, Flex } from "@/ui/components/layout";
import { Button } from "@/ui/components/primitives/Button";
import { Icon } from "@/ui/components/primitives/Icon";
import { Separator } from "@/ui/components/primitives/Separator";
import { TextBody } from "@/ui/components/typography/TextBody";
import { TextSubtitle } from "@/ui/components/typography/TextSubtitle";

import {
  CAT_MEDICAL_CARD_ARTICLE_CLASS,
  CAT_MEDICAL_CARD_DEFAULTS,
  type TCatMedicalCardProps,
} from "./CatMedicalCard.config";
import { useCatMedicalCard } from "./useCatMedicalCard";

export const CatMedicalCard: FC<TCatMedicalCardProps> = ({
  allergies,
  medicalConditions,
  onEdit,
  "data-component": component = CAT_MEDICAL_CARD_DEFAULTS["data-component"],
}) => {
  const {
    title,
    allergiesLabel,
    medicalConditionsLabel,
    editCopy,
    editAriaLabel,
  } = useCatMedicalCard();

  return (
    <Article
      data-component={component}
      className={CAT_MEDICAL_CARD_ARTICLE_CLASS}
    >
      <Flex align="center" justify="between" gap="4">
        <TextSubtitle tag="h2" size={2}>
          { title }
        </TextSubtitle>
        <Button aria-label={editAriaLabel} onClick={onEdit} variant="tertiary">
          { editCopy }
        </Button>
      </Flex>

      <Flex align="end" className="mt-4" gap="3">
        <Container className="flex-1">
          <TextBody color="secondary" size={3}>
            { allergiesLabel }
          </TextBody>
          <TextSubtitle className="mt-1 whitespace-pre-line break-words" size={4}>
            { allergies }
          </TextSubtitle>

          <Separator className="my-2" />

          <TextBody color="secondary" size={3}>
            { medicalConditionsLabel }
          </TextBody>
          <TextSubtitle className="mt-1 whitespace-pre-line break-words" size={4}>
            { medicalConditions }
          </TextSubtitle>
        </Container>

        <Icon
          aria-hidden="true"
          className="w-24 aspect-auto"
          icon="vetAndCat"
        />
      </Flex>
    </Article>
  );
};

CatMedicalCard.displayName = "CatMedicalCard";
